/**
 * Feature flags - decided by the server, overridable from the URL.
 *
 * A flag says whether a feature is switched on *for this account* in *this
 * deployment*. That answer comes from `cfg.feature_flag` and its targeting
 * rules (per user, per group, per environment) and rides along on `GET /api/me`
 * as `flags`, so it is known before the navigation is built. Rules and the
 * evaluation function are documented in `docs/plans/postgresql.md`.
 *
 * Three sources, in order of precedence:
 *
 * 1. **`?ff=` in the URL** - the development override for a run with no
 *    database: comma-separated names, `-name` to force one off. Honoured only
 *    until the server says it holds a managed policy for this account
 *    (`serverManaged`), and by the *server* only on a local run with no
 *    database (it travels on the `X-Feature-Flags` header). Wherever Features
 *    is stored it is authoritative - for administrators and Root too
 *    (`docs/plans/access-management.md`).
 * 2. **The server's map**, once `/api/me` answers.
 * 3. **`DEFAULTS`** below, for the moment before it does, and for a deployment
 *    with no database at all. They are the app as it behaved before flags
 *    existed, which is what a database outage must degrade to.
 *
 * Keep `DEFAULTS` in step with `backend/flags.py` and the seed in migration
 * `0007_cfg_flags`. Maintain the user-facing list in `frontend/README.md`.
 */

import { Atom } from './atom.ts';

/**
 * Known flags. Page flags share the `PageId` names on purpose - the page and
 * the switch that governs it are the same thing named twice otherwise.
 */
export type FeatureFlag =
  | 'plot'
  | 'export'
  | 'modelviewer'
  | 'dashboards'
  | 'assistant'
  | 'live'
  | 'argus'
  | 'sankey'
  | 'livestatus';

/** What each flag means with no server answer yet, and with no database ever. */
export const DEFAULTS: Readonly<Record<FeatureFlag, boolean>> = {
  plot: true,
  export: true,
  modelviewer: true,
  // Administrators only, and nobody is an administrator without a database -
  // see `backend/flags.py`, which carries the same table.
  dashboards: false,
  assistant: false,
  live: false,
  // Argus belongs to one team and is made of stored state - see `docs/plans/argus.md`.
  // Off here means absent, not disabled: no switch, no rooms, no mention.
  argus: false,
  // The Sankey plot type, in development: administrators only (migration 0053).
  sankey: false,
  // The Live status page: administrators first (migration 0054).
  livestatus: false,
};

const KNOWN_FLAGS = Object.keys(DEFAULTS) as FeatureFlag[];

function parseOverrides(): Map<FeatureFlag, boolean> {
  const out = new Map<FeatureFlag, boolean>();
  if (typeof location === 'undefined') return out;
  const raw = new URLSearchParams(location.search).get('ff') ?? '';
  for (const entry of raw.split(',')) {
    let name = entry.trim();
    if (!name) continue;
    let value = true;
    if (name.startsWith('-') || name.startsWith('!')) {
      name = name.slice(1).trim();
      value = false;
    }
    if ((KNOWN_FLAGS as readonly string[]).includes(name)) out.set(name as FeatureFlag, value);
  }
  return out;
}

/**
 * URL overrides for this session, parsed once at load - before the first paint,
 * so a deep link renders the right tabs without waiting for the network.
 */
export const overrides: ReadonlyMap<FeatureFlag, boolean> = parseOverrides();

/** Flags forced on by `?ff=`, for `buildQueryString` to re-emit. */
export const activeFlags: ReadonlySet<FeatureFlag> = new Set(
  [...overrides].filter(([, on]) => on).map(([name]) => name),
);

/** The server's answer, empty until `/api/me` returns. */
export const serverFlags = new Atom<Readonly<Partial<Record<FeatureFlag, boolean>>>>({});

/**
 * Whether the server holds a managed policy for this account (a database knows
 * who they are). Then Features is authoritative and `?ff=` is ignored here as
 * it is on the server - a link cannot switch on what Features switched off.
 */
export const serverManaged = new Atom<boolean>(false);

/** Store what the server said. Called from `loadMe()` on every refresh. */
export function setServerFlags(map: Record<string, unknown> | null | undefined, managed = false): void {
  serverManaged.set(managed);
  const next: Partial<Record<FeatureFlag, boolean>> = {};
  for (const name of KNOWN_FLAGS) {
    const value = map?.[name];
    if (typeof value === 'boolean') next[name] = value;
  }
  serverFlags.set(next);
}

/** Whether a feature is on for this session. */
export function hasFlag(flag: FeatureFlag): boolean {
  const override = overrides.get(flag);
  if (override !== undefined && !serverManaged.value) return override;
  const fromServer = serverFlags.value[flag];
  return fromServer ?? DEFAULTS[flag];
}

/**
 * The override header for API calls, so the server agrees with the browser
 * about what is switched on. It decides for itself whether to honour it.
 */
export function flagOverrideHeader(): Record<string, string> {
  if (!overrides.size) return {};
  const value = [...overrides].map(([name, on]) => (on ? name : `-${name}`)).join(',');
  return { 'X-Feature-Flags': value };
}
